import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  styled,
  CircularProgress,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { Controller, FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { ICandidate, indexMapping, submitCandidate } from 'wisestep-api';
import {
  ChatBoxHeader,
  ControlledSalaryDetails,
  ControlledTextArea,
  ControlledTextInput,
  IMessengerType,
  Item,
  Label,
  MessengerInput,
  TagsInput,
  WsButton,
  WsDialog,
  WsLocationSearch,
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  ControlledWsButtonGroup,
} from 'wisestep-base';

import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import ActionFooter from './ActionFooter';
import DefaultEmailModal from './DefaultEmailModal';

export interface IComponentProps {
  onClose: () => void;
  candidate: ICandidate;
  onSubmit: (candidate: ICandidate, proceed: boolean) => void;
  selectedPlaceholders: {
    [name: string]: string;
  };
  isMapping: boolean;
}
const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '10px',
}));
const FormTagsInput = styled(TagsInput)(() => ({
  '.MuiOutlinedInput-root': {
    padding: '4.5px 8px',
    margin: 0,
  },
}));

export const JobType: WsSelectObj[] = [
  {
    label: 'Permanent',
    value: 'Permanent',
  },
  {
    label: 'Contract',
    value: 'Contract',
  },
  {
    label: 'Temporary',
    value: 'Temporary',
  },
  {
    label: 'Internship',
    value: 'Internship',
  },
];

export const NoticePeriod: WsSelectObj[] = [
  {
    label: 'Immediate',
    value: 'Immediate',
  },
  {
    label: 'Within 15 Days',
    value: 'Within_15_Days',
  },
  {
    label: 'Within 1 Month',
    value: 'Within_1_Month',
  },
  {
    label: 'Within 2 Months',
    value: 'Within_2_Months',
  },
  {
    label: 'Within 3 Months',
    value: 'Within_3_Months',
  },
  {
    label: 'Within 4 Months',
    value: 'Within_4_Months',
  },
];

export const RelocationPreference: WsSelectObj[] = [
  {
    label: 'No Relocation',
    value: 'NoRelocation',
  },
  {
    label: 'Within State',
    value: 'WithinState',
  },
  {
    label: 'Within Country',
    value: 'WithinCountry',
  },
  {
    label: 'Any Where',
    value: 'AnyWhere',
  },
];

export const jobPreference: WsSelectObj[] = [
  {
    label: 'Permanent',
    value: 'PERMANENT',
  },
  {
    label: 'Contract',
    value: 'CONTRACT',
  },
];

const EditCandidateDrawer = ({
  onClose,
  candidate,
  onSubmit,
  selectedPlaceholders,
  isMapping,
}: IComponentProps) => {
  const methods = useForm<ICandidate>({
    defaultValues: { ...candidate },
  });
  const [currentSalaryValue, setCurrentSalaryValue] = useState<Record<string, string>>();
  const [expectedSalaryValue, setExpectedSalaryValue] = useState<Record<string, string>>();
  const { handleSubmit, register } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [showMissingPlaceholderPopup, setShowMissingPlaceholderPopup] = useState<boolean>(false);
  const [updatedCandidate, setUpdatedCandidate] = useState<ICandidate | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  register('currentSalary', {
    onChange: (e) => {
      const data = e.target.value;
      if (data) {
        setCurrentSalaryValue(data as Record<string, string>);
      }
    },
  });

  register('willingToTravel', {
    onChange: (e) => {
      methods.setValue('willingToTravel', e.target.value === 'true' ? true : false);
    },
  });

  register('familyWillingToRelocate', {
    onChange: (e) => {
      methods.setValue('familyWillingToRelocate', e.target.value === 'true' ? true : false);
    },
  });

  register('interviewingWithOthers', {
    onChange: (e) => {
      methods.setValue('interviewingWithOthers', e.target.value === 'true' ? true : false);
    },
  });

  register('expectedBaseSalary', {
    onChange: (e) => {
      const data = e.target.value;
      if (data) {
        setExpectedSalaryValue(data as Record<string, string>);
      }
    },
  });

  register('jobPreferencePermanent', {
    onChange: (e) => {
      const data = e.target.value;
      if (data.index == 0) {
        methods.setValue('jobPreferenceContract', false);
        methods.setValue('jobPreferencePermanent', true);
      } else {
        methods.setValue('jobPreferenceContract', true);
        methods.setValue('jobPreferencePermanent', false);
      }
    },
  });

  const onFormSubmit = async (data: ICandidate) => {
    setIsLoading(true);
    if (data.currentSalary && currentSalaryValue) {
      data.currentSalary = parseFloat(currentSalaryValue['value'] ?? 0);
      data.currentSalaryCurrency = currentSalaryValue['currency'];
      data.currentSalaryTimeSpan = currentSalaryValue['timeSpan'];
    }
    if (data.expectedBaseSalary && expectedSalaryValue) {
      data.expectedBaseSalary = parseFloat(expectedSalaryValue['value'] ?? 0);
      data.expectedSalaryCurrency = expectedSalaryValue['currency'];
      data.expectedBaseSalaryTimeSpan = expectedSalaryValue['timeSpan'];
    }
    let hasMissingValues = false;
    for (const key of Object.keys(selectedPlaceholders)) {
      const value = data[selectedPlaceholders[key].split('|')[0].trim()];
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value === 'object' && isEmpty(value)) ||
        (typeof value === 'number' && Number.isNaN(value)) ||
        (typeof value === 'boolean' && value === null)
      ) {
        hasMissingValues = true;
        break;
      }
    }

    if (hasMissingValues) {
      setUpdatedCandidate(data);
      setShowMissingPlaceholderPopup(true);
    } else {
      const resp = await indexMapping(data);
      onSubmit(resp.data, true);
      onClose();
      setNotification(success('Candidate successfully updated.'));
    }
    setIsLoading(false);
  };

  const exists = (placeholder: string): boolean => {
    return placeholder in selectedPlaceholders;
  };

  const updateCandidate = async () => {
    if (updatedCandidate) {
      let resp;
      if (isMapping) {
        resp = await indexMapping(updatedCandidate);
      } else {
        resp = await submitCandidate(updatedCandidate);
      }
      onSubmit(resp.data, true);
      onClose();
      setShowMissingPlaceholderPopup(false);
      setNotification(success('Candidate successfully updated.'));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit as SubmitHandler<FieldValues>)}>
          <ChatBoxHeader>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1}>
                  <WsTypography
                    variant={WsTypoVariants.TITLE}
                    color={WsTypoColors.PRIMARY}
                    bold={true}
                  >
                    Update Candidate
                  </WsTypography>
                </Box>
              </Grid>
            </Grid>
          </ChatBoxHeader>

          <Grid item xs={12} sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            {exists('CANDIDATE_NAME') && (
              <GridItem sx={{ mt: '12px' }}>
                <ControlledTextInput
                  name="name"
                  labelText="Name"
                  fullWidth
                  id="candidate_name"
                  placeholder="Enter Candidate Name"
                  variant="outlined"
                  autoComplete="candidate_name"
                />
              </GridItem>
            )}
            {exists('CANDIDATE_EMAIL') && (
              <GridItem>
                <ControlledTextInput
                  name="email"
                  labelText="Email address"
                  type="email"
                  fullWidth
                  id="candidate_email"
                  placeholder="Enter Candidate Email Address"
                  variant="outlined"
                  autoComplete="candidate_email"
                />
              </GridItem>
            )}
            {exists('CANDIDATE_PHONE') && (
              <GridItem>
                <ControlledTextInput
                  name="phoneNumber"
                  labelText="Phone Number"
                  type="number"
                  fullWidth
                  id="candidate_phone"
                  placeholder="Enter Candidate Phone Number"
                  variant="outlined"
                  autoComplete="candidate_phone"
                />
              </GridItem>
            )}
            {exists('CANDIATE_STATE') && (
              <GridItem>
                <Label>Current Location</Label>
                <WsLocationSearch
                  placeHolder=""
                  name="currentLocation"
                  variant="outlined"
                  multiSelect={false}
                  showMandatorySymbol={false}
                />
              </GridItem>
            )}
            {exists('LINKEDIN_URL') && (
              <GridItem>
                <ControlledTextInput
                  name="linkedInURL"
                  type="url"
                  labelText="LinkedIn URL"
                  fullWidth
                  id="linked_in_url"
                  variant="outlined"
                />
              </GridItem>
            )}
            {exists('MESSENGERS') && (
              <GridItem>
                <MessengerInput
                  initialValue={[{ type: '', messengerId: '' }]}
                  onInputChange={(tag: IMessengerType[]) => console.log(tag)}
                />
              </GridItem>
            )}
            {exists('SKILLS') && (
              <GridItem>
                <Label>Skills</Label>
                <FormTagsInput
                  name="skills"
                  placeholder="Enter Skills"
                  multiple={true}
                  initialValue={[]}
                  onTagsChange={(tag: string[]) => console.log(tag)}
                />
              </GridItem>
            )}
            {exists('WORK_AUTHORIZATION') && (
              <GridItem>
                <ControlledTextInput
                  name="workAuthorization"
                  labelText="Work Authorization Category"
                  fullWidth
                  id="work_Authorization"
                  variant="outlined"
                />
              </GridItem>
            )}
            {exists('CANDIDATE_SOURCE') && (
              <GridItem sx={{ mt: '12px' }}>
                <ControlledTextInput
                  name="source"
                  labelText="Source"
                  fullWidth
                  id="candidate_source"
                  placeholder="Enter source"
                  variant="outlined"
                  autoComplete="candidate_source"
                />
              </GridItem>
            )}
            {exists('NOTICE_PERIOD') && (
              <>
                <GridItem>
                  <WsSelect name="noticePeriod" label="Notice Period" selectArray={NoticePeriod} />
                </GridItem>
              </>
            )}
            {exists('CANDIDATE_TOTAL_EXP') && (
              <>
                <GridItem>
                  <Label>{`Total experience`}</Label>
                </GridItem>
                <GridItem>
                  <ControlledTextInput
                    name="totalYearsExperience"
                    type="number"
                    fullWidth
                    id="candidate_exp_in_year"
                    placeholder="Enter Years"
                    variant="outlined"
                  />
                </GridItem>
              </>
            )}
            {exists('CANDIDATE_RELEV_EXP') && (
              <>
                <GridItem>
                  <Label>Relevant experience</Label>
                </GridItem>
                <GridItem>
                  <ControlledTextInput
                    name="relevantYearsExperience"
                    type="number"
                    fullWidth
                    id="candidate_exp_in_year"
                    placeholder="Enter Years"
                    variant="outlined"
                    autoComplete="candidate_exp_in_year"
                  />
                </GridItem>
              </>
            )}
            {exists('CANDIDATE_CURRENT_JOB') && (
              <GridItem>
                <ControlledTextInput
                  name="currentJobTitle"
                  labelText={'Current Job Title'}
                  fullWidth
                  id="candidate_jobTitle"
                  placeholder="Enter Current Job Title"
                  variant="outlined"
                  autoComplete="candidate_jobTitle"
                />
              </GridItem>
            )}
            {exists('CANDIDATE_CURR_EMPLOYER') && (
              <GridItem>
                <ControlledTextInput
                  name="currentEmployer"
                  labelText={'Current Employer'}
                  fullWidth
                  id="candidate_currentCompany"
                  placeholder="Enter Current Employer"
                  variant="outlined"
                  autoComplete="candidate_currentCompany"
                />
              </GridItem>
            )}
            {exists('CANDIDATE_JOB_TYPE') && (
              <GridItem>
                <WsSelect name="currentJobType" label="Current Job Type" selectArray={JobType} />
              </GridItem>
            )}
            {exists('CANDIDATE_OTH_CURR_ALLOWANCE') && (
              <GridItem>
                <ControlledTextInput
                  name="otherCurrentAllowances"
                  labelText={'Current Allowances and Benefits'}
                  fullWidth
                  id="current_allowance"
                  placeholder="Enter current allowances and benefits"
                  variant="outlined"
                  autoComplete="current_allowance"
                />
              </GridItem>
            )}
            {exists('CANDIDATE_PREV_JOB') && (
              <GridItem>
                <Label>Previous Job Titles</Label>
                <FormTagsInput
                  name="previousJobTitles"
                  placeholder="Enter previous job titles"
                  multiple={true}
                  initialValue={[]}
                />
              </GridItem>
            )}
            {exists('CANDIDATE_PREV_EMPLOYERS') && (
              <GridItem>
                <Label>Previous Employers</Label>
                <FormTagsInput
                  name="previousEmployers"
                  placeholder="Enter previous employers"
                  multiple={true}
                  initialValue={[]}
                />
              </GridItem>
            )}
            {exists('EDUCATION') && (
              <GridItem>
                <Label>Highest education qualifications</Label>
                <FormTagsInput
                  name="education"
                  placeholder="Enter qualifications"
                  multiple={true}
                  initialValue={[]}
                />
              </GridItem>
            )}
            {exists('WILLING_TO_TRAVEL') && (
              <>
                <GridItem sx={{ mt: '12px' }} id="willingToTravel">
                  <Label>Willing To Travel</Label>
                  <FormControl component="fieldset">
                    <Controller
                      name="willingToTravel"
                      control={methods.control}
                      defaultValue={true}
                      render={({ field }) => (
                        <RadioGroup {...field} row>
                          {[true, false].map((option) => (
                            <FormControlLabel
                              key={option.toString()}
                              value={option}
                              control={<Radio />}
                              label={option === true ? 'Yes' : 'No'}
                            />
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <WsLocationSearch
                    placeHolder="Enter preferred city"
                    name="preferredLocations"
                    variant="outlined"
                    multiSelect={true}
                    showMandatorySymbol={false}
                  />
                </GridItem>
              </>
            )}
            {exists('RELOCATION_PREF') && (
              <>
                <GridItem sx={{ mt: '12px' }} id="willingToTravel">
                  <Label>Relocation Preference</Label>
                  <WsSelect
                    name="relocationPreference"
                    label=""
                    selectArray={RelocationPreference}
                  />
                </GridItem>
              </>
            )}
            {exists('FAMILY_WILLING_TO_RELOCATE') && (
              <>
                <GridItem sx={{ mt: '12px' }} id="familyWillingToRelocate">
                  <Label>Family willing to relocate</Label>
                  <FormControl component="fieldset">
                    <Controller
                      name="familyWillingToRelocate"
                      control={methods.control}
                      defaultValue={true}
                      render={({ field }) => (
                        <RadioGroup {...field} row>
                          {[true, false].map((option) => (
                            <FormControlLabel
                              key={option.toString()}
                              value={option}
                              control={<Radio />}
                              label={option === true ? 'Yes' : 'No'}
                            />
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                </GridItem>
              </>
            )}
            {exists('VISA_STATUS') && (
              <GridItem>
                <ControlledTextInput
                  name="visaStatus"
                  labelText="Visa Status"
                  type="text"
                  fullWidth
                  id="visaStatus"
                  placeholder="Enter visa status"
                  variant="outlined"
                  autoComplete="marginTop"
                />
              </GridItem>
            )}
            {exists('VISA_VALIDITY') && (
              <GridItem>
                <ControlledTextInput
                  name="visaValidity"
                  labelText="Visa Validity"
                  type="text"
                  fullWidth
                  id="visaValidity"
                  placeholder="Enter visa validity"
                  variant="outlined"
                  autoComplete="marginTop"
                />
              </GridItem>
            )}
            {exists('CANDIDATE_CURRENT_SALARY') && (
              <GridItem sx={{ mt: '12px' }}>
                <Label>Current Salary</Label>
                <ControlledSalaryDetails name="currentSalary" />
              </GridItem>
            )}
            {exists('CANDIDATE_EXP_BASE_SAL') && (
              <GridItem sx={{ mt: '12px' }}>
                <Label>Expected Salary</Label>
                <ControlledSalaryDetails name="expectedBaseSalary" />
              </GridItem>
            )}
            {exists('CANDIDATE_OTH_EXP_ALLOWANCE') && (
              <GridItem>
                <ControlledTextInput
                  name="otherExpectedAllowances"
                  labelText={'Expected allowances and benefits'}
                  fullWidth
                  id="expected_allowance"
                  placeholder="Enter expected allowances and benefits"
                  variant="outlined"
                  autoComplete="expected_allowance"
                />
              </GridItem>
            )}
            {exists('INTERVIEWING_WITH_OTHERS') && (
              <GridItem sx={{ mt: '12px' }} id="interviewingWithOthers">
                <Label>Interviewing With Others</Label>
                <FormControl component="fieldset">
                  <Controller
                    name="interviewingWithOthers"
                    control={methods.control}
                    defaultValue={true}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        {[true, false].map((option) => (
                          <FormControlLabel
                            key={option.toString()}
                            value={option}
                            control={<Radio />}
                            label={option === true ? 'Yes' : 'No'}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </GridItem>
            )}

            {exists('REASON_FOR_NEW_JOB') && (
              <GridItem>
                <ControlledTextArea
                  name="reasonForNewJob"
                  labelText="Reason for looking for a new job"
                />
              </GridItem>
            )}
            {exists('ASSESSMENT') && (
              <GridItem>
                <ControlledTextArea name="assessment" labelText="Recruiters assessment Notes" />
              </GridItem>
            )}
            {exists('CANDIDATE_JOB_PREF_PERM') && (
              <GridItem>
                <ControlledWsButtonGroup
                  name="jobPreferencePermanent"
                  inputTitle="Job Preference"
                  buttonsList={['Permanent', 'Contract']}
                />
              </GridItem>
            )}
            <ActionFooter>
              <Grid item container xs={6} pr={2} justifyContent="flex-end">
                <WsButton
                  size="large"
                  type="submit"
                  disabled={isLoading}
                  endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : undefined}
                >
                  {isLoading ? 'Saving...' : 'Update'}
                </WsButton>
              </Grid>
            </ActionFooter>
          </Grid>
        </form>
      </FormProvider>
      {showMissingPlaceholderPopup && (
        <WsDialog
          openDialog={showMissingPlaceholderPopup}
          onClose={() => setShowMissingPlaceholderPopup(false)}
        >
          <DefaultEmailModal
            title="Incomplete candidate information"
            text="Not all the values for the candidate are provided, would you like to proceed?"
            content=""
            confirm={() => {
              updateCandidate();
            }}
            close={() => {
              setShowMissingPlaceholderPopup(false);
            }}
            confirmButtonText="Proceed"
          ></DefaultEmailModal>
        </WsDialog>
      )}
    </>
  );
};
export default EditCandidateDrawer;
